import { getQueryParam } from "@/utils/queryParam";
import { APP_SCHEMES, hasScheme } from "@/utils/returnToApp";

/**
 * Schemes `onSuccessUrl` may use: the web, and the MATCHi app's own schemes.
 *
 * `onSuccessUrl` arrives in the query string, so anyone can craft a checkout
 * link carrying one. It is then navigated to and rendered as an `href`, on a
 * page that holds the payer's token. A `javascript:` or `data:` URL there would
 * run in our origin, so anything outside this list is dropped.
 *
 * This blocks script schemes only. Any http(s) host is still accepted, by
 * design: the widget passes whichever page it is embedded in.
 */
const ALLOWED_SCHEMES = ["https:", "http:", ...APP_SCHEMES];

/**
 * `onSuccessUrl` if it is safe to send the payer to, otherwise null — the payer
 * then lands on our own success page, as if no URL had been passed.
 */
export const getOnSuccessUrl = () => {
  const onSuccessUrl = getQueryParam("onSuccessUrl");
  return onSuccessUrl && hasScheme(onSuccessUrl, ALLOWED_SCHEMES)
    ? onSuccessUrl
    : null;
};

/**
 * Where a confirmed payment sends the payer: back to whoever opened the
 * checkout, or on to our own success page.
 *
 * `handBackToCaller` is the contract with our callers, and it defaults to true
 * because the MATCHi app relies on that default — it passes `onSuccessUrl` and
 * omits `autoRedirect` entirely (see `browser.ts` in mobileapp). Only a caller
 * that explicitly sends `autoRedirect=false` gets our success page instead,
 * which is what the widget does unless the integrator set a `redirectUrl`
 * option.
 *
 * That default is load-bearing for the return-to-app fallback in
 * `returnToApp.ts`: the fallback only exists on the hand-back path, so a client
 * that started sending `autoRedirect=false` would silently drop it. Resolved in
 * one place so the three post-payment sites cannot drift apart on it.
 */
export const resolveSuccessTarget = (token: string) => {
  const onSuccessUrl = getOnSuccessUrl();
  const autoRedirect = getQueryParam("autoRedirect");

  if (onSuccessUrl && autoRedirect !== "false")
    return { handBackToCaller: true as const, url: onSuccessUrl };

  return {
    handBackToCaller: false as const,
    url: onSuccessUrl
      ? `/pay/${token}/success?${new URLSearchParams({ onSuccessUrl })}`
      : `/pay/${token}/success`,
  };
};
